import { Box, Flex, IconButton } from "@chakra-ui/react"
import { EDITOR_MODE } from "@pathwright/blocks-engine"
import { forwardRef } from "react"
import { useFlashcard } from "./FlashcardProvider"

/*
	Flashcard z-index hierarchy:
	9 - DragSquare
	10 - FlashcardImageWrapper
	11 - FlashcardHTMLWrapper
	12 - Buttons
*/

export const gray = "#fafafa"

export const FlashcardButton = forwardRef(
  ({ children, onImage, isFlipButton, icon, ...props }, ref) => {
    const { editingText } = useFlashcard()

    return (
      <IconButton
        aria-label={isFlipButton ? "Flip Flashcard" : "Flashcard Button"}
        ref={ref}
        className="show-on-hover"
        // Solves the dissappearing flashcard bug in Safari
        // https://stackoverflow.com/questions/32875046/ios-9-safari-changing-an-element-to-fixed-position-while-scrolling-wont-paint/43188105#43188105
        transform="translateZ(0)"
        alignItems="center"
        position="absolute"
        transition="opacity 0.4s"
        height="30px"
        zIndex={editingText ? 0 : 12}
        opacity={editingText ? 0 : 1}
        borderRadius="100px"
        p="0 1rem"
        minWidth="2em"
        variant="surface"
        colorPalette="gray"
        {...props}
      >
        {children || icon}
      </IconButton>
    )
  }
)

export const FlashcardAudioWrap = ({ children, ...props }) => (
  <Flex
    justify="space-between"
    align="center"
    position="absolute"
    top="0.5rem"
    left="0.5rem"
    zIndex="13"
    {...props}
  >
    {children}
  </Flex>
)

export const FlashcardRemove = ({ children, ...props }) => (
  <FlashcardButton
    top="0.5rem"
    right="0.5rem"
    zIndex="12"
    css={{
      "& .Button__content-wrapper": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }
    }}
    {...props}
  >
    {children}
  </FlashcardButton>
)

export const FlashcardAudioChange = ({ children, ...props }) => (
  <FlashcardButton
    position="relative !important"
    marginRight="0.3rem"
    {...props}
  >
    {children}
  </FlashcardButton>
)

export const FlashcardSideWrapper = ({ children, ...props }) => (
  <Box
    position="absolute"
    width="100%"
    height="100%"
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    backgroundColor="#fff"
    {...props}
  >
    {children}
  </Box>
)

export const FlashcardImagePicker = ({ children, ...props }) => (
  <FlashcardButton
    bottom={"0.5rem"}
    left={"0.5rem"}
    zIndex={13}
    pointerEvents={"all"}
    {...props}
  >
    {children}
  </FlashcardButton>
)

export const FlashcardHTMLWrapper = ({
  children,
  applyOverlay,
  mode,
  scaledFontSize,
  ...props
}) => (
  <Box
    h="100%"
    w="100%"
    bg={applyOverlay ? "rgba(0,0,0,0.2)" : ""}
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    p="2rem 1rem"
    // In edit mode, let pointer events fall through to the Quill editor or DragSquare
    // In view mode, the whole card is clickable/flippable
    pointerEvents={mode === EDITOR_MODE.EDIT ? "none" : "all"}
    transition="background-color 0.3s"
    zIndex={mode === EDITOR_MODE.VIEW ? 13 : 11}
    css={{
      "& > div, & .ContentText": {
        width: "100%",
        height: mode === EDITOR_MODE.VIEW ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        // Parent opts out pointer events, so opt in to pointer events for content elements
        pointerEvents:"all",
        overflow: "visible",

        a: {
          // Parent opts out pointer events, so opt in to pointer events for links
          pointerEvents: "all"
        }
      },

      "& .ql-editor:before": {
        color: "#000 !important"
      },

      "& .ql-editor": {
        overflow: "visible",
        wordWrap: "break-word",
        // Parent opts out pointer events, so opt in to pointer events for editor content
        pointerEvents:"all",

        "p, &:before": {
          color: "#000 !important"
        },

        ".ql-picker-options.ql-bubble.ql-color-picker": {
          width: "auto !important"
        },

        ".ql-expanded .ql-picker-options": {
          width: "auto !important",
          display: "flex !important"
        },

        ".ql-toolbar button, .ql-toolbar .ql-picker": {
          height: "22px",
          width: "26px"
        }
      },

      /*
        Solves a Firefox bug where tooltip buttons render oddly
        This happens because pseudo elements appear to inherit line height
      */
      "& .ql-toolbar::after, .ql-toolbar .ql-formats::after": {
        lineHeight: "0 !important"
      },

      "& .ql-editor > p": {
        fontSize: scaledFontSize ? `${scaledFontSize}px` : "1.2em",
        lineHeight: "1.2",
        textAlign: "center",
        overflowWrap: "break-word"
      },

      "& .BlockText": {
        fontSize: scaledFontSize
          ? `${scaledFontSize}px !important`
          : `1.2em !important`,
        lineHeight: "1.2 !important",
        textAlign: "center",
        textOverflow: "",
        whiteSpace: "normal",

        ul: {
          textAlign: "left"
        }
      },

      "& .ql-editor > p::before": {
        textAlign: "center"
      },

      /* Hack for Safari to make sure white color is applied in Safari */
      "& span[data-value='rgba(255,255,255)']": {
        backgroundColor: "white !important"
      }
    }}
    {...props}
  >
    {children}
  </Box>
)

export const FlashcardImageWrapper = ({ children, ...props }) => (
  <Box
    maxH="100%"
    className="FlashcardImageWrapper"
    // Block events on Flashcard image (it's a background image)
    pointerEvents="none"
    {...props}
    css={{
      "& .BackgroundImage": {
        zIndex: 10
      },
      "& .Blocks__ImageControls": {
        // Parent opts out pointer events, so opt in to pointer events for image controls
        pointerEvents: "all",
        top: "auto",
        bottom: 0,
        zIndex: 11
      },
      "& .BackgroundImage__empty": {
        backgroundColor: "transparent"
      }
    }}
  >
    {children}
  </Box>
)

export const DragSquare = ({ children, ...props }) => (
  <Box
    h="100%"
    w="100%"
    pos="absolute"
    top="0"
    left="0"
    right="0"
    bottom="0"
    zIndex="9"
    cursor="move"
    _active={{ cursor: "grabbing" }}
    _hover={{ cursor: "grab" }}
    {...props}
  >
    {children}
  </Box>
)
